import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  BadgeCheck,
  Check,
  Crown,
  Eye,
  FileCheck2,
  Handshake,
  Lock,
  Trash2,
  UserCheck,
  HandCoins,
  MapPin,
  MessageCircle,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Store,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import {
  Link,
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useParams,
} from "react-router-dom";

import {
  publicApi,
  type Plan,
  type PublicStokvel,
} from "@/api/endpoints/stokvels";
import { SearchInput, SkeletonRows } from "@/components/ui";
import { cn } from "@/lib/cn";

import { StokkieLogo } from "../stokvel/StokkieLogo";
import { PaidRing, Photo, PriceLadder, StepFlow, type PhotoName } from "./ui";
import { PARTNER } from "./partner";
import { useStokkieTitle } from "./useStokkieTitle";

/**
 * The Stokkie Stokvel website — the public face of the stokvel portal, on the same
 * domain as the app it leads into.
 *
 * Written to the Phase 1 scope's guardrails, which are what separate Stokkie Stokvel
 * from an investment platform: it says what a stokvel can *do* here, never what
 * its money will earn. No return figures, no "invest now", no amounts held —
 * because none are. Groups pay their own bank accounts and their own suppliers;
 * Stokkie Stokvel keeps them organised and connected.
 */
export function StokkieLayout() {
  useStokkieTitle();
  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <header className="sticky top-0 z-20 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex h-[4.5rem] max-w-6xl items-center gap-4 px-4">
          <Link
            to="/stokkie"
            aria-label="Stokkie Stokvel home"
            className="shrink-0"
          >
            <StokkieLogo className="h-12" />
          </Link>
          <nav
            aria-label="Stokkie Stokvel"
            className="ml-auto hidden items-center gap-5 text-sm sm:flex"
          >
            <TopLink to="/stokkie" end>
              Home
            </TopLink>
            <TopLink to="/stokkie/stokvels">Find a stokvel</TopLink>
            <TopLink to="/stokkie/pricing">Pricing</TopLink>
            <TopLink to="/stokkie/safety">Safety &amp; POPIA</TopLink>
          </nav>
          <Link
            to="/stokvel/start"
            className="ml-auto inline-flex h-10 items-center rounded-control bg-brand-500 px-4 text-sm font-semibold text-white hover:bg-brand-600 sm:ml-0"
          >
            Sign in
          </Link>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-line bg-surface">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-8 text-sm text-ink-500 sm:flex-row sm:items-center">
          <StokkieLogo className="h-11" />
          <nav
            aria-label="More"
            className="flex flex-wrap gap-x-5 gap-y-2 font-semibold sm:ml-6"
          >
            <Link to="/stokkie/stokvels" className="hover:text-ink-900">
              Find a stokvel
            </Link>
            <Link to="/stokkie/pricing" className="hover:text-ink-900">
              Pricing
            </Link>
            <Link to="/stokkie/safety" className="hover:text-ink-900">
              Safety &amp; POPIA
            </Link>
          </nav>
          <p className="sm:ml-auto sm:max-w-xs sm:text-right">
            Stokkie Stokvel by Jalusi. Stokkie Stokvel does not hold, invest or
            move stokvel money.
          </p>
        </div>
      </footer>
    </div>
  );
}

function TopLink({
  to,
  end,
  children,
}: {
  to: string;
  end?: boolean;
  children: ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end ?? false}
      className={({ isActive }) =>
        cn(
          "font-medium",
          isActive ? "text-brand-500" : "text-ink-600 hover:text-ink-900",
        )
      }
    >
      {children}
    </NavLink>
  );
}

// ─── Home ─────────────────────────────────────────────────────────────────────
const STEPS: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: Smartphone,
    title: "Sign up",
    text: "Just your cellphone number and a PIN by SMS.",
  },
  {
    icon: Users,
    title: "Start or join",
    text: "Start your stokvel, or join with an invite code.",
  },
  {
    icon: BadgeCheck,
    title: "Track together",
    text: "Everybody sees who has paid this month.",
  },
  {
    icon: ShoppingCart,
    title: "Buy and sell",
    text: "Bulk buys and a market for members.",
  },
];

const PROMISES: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: ShieldCheck,
    title: "Your money stays yours",
    text: "Stokkie Stokvel never holds stokvel money.",
  },
  {
    icon: Lock,
    title: "POPIA compliant",
    text: "We collect only what we need, and only with your consent.",
  },
  {
    icon: MessageCircle,
    title: "Your number stays private",
    text: "Only shared when you choose.",
  },
  {
    icon: Smartphone,
    title: "Works on any phone",
    text: "In the browser. Nothing to download.",
  },
];

/** A section with a photograph on one side and one idea on the other. */
function PhotoSection({
  photo,
  alt,
  eyebrow,
  title,
  text,
  flip,
  children,
}: {
  photo: PhotoName;
  alt: string;
  eyebrow: string;
  title: string;
  text: string;
  flip?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="mx-auto grid max-w-6xl items-center gap-8 px-4 py-12 lg:grid-cols-2 lg:gap-14 lg:py-16">
      <Photo
        name={photo}
        alt={alt}
        className={cn(
          "h-64 w-full rounded-3xl shadow-pop sm:h-80",
          flip && "lg:order-2",
        )}
      />
      <div>
        <p className="text-sm font-bold uppercase tracking-wider text-gold-700">
          {eyebrow}
        </p>
        <h2 className="mt-2 text-3xl font-extrabold leading-tight text-ink-900">
          {title}
        </h2>
        <p className="mt-3 text-lg text-ink-600">{text}</p>
        <div className="mt-6">{children}</div>
      </div>
    </section>
  );
}

export function StokkieHome() {
  const stokvels = useQuery({
    queryKey: ["public-stokvels"],
    queryFn: () => publicApi.stokvels(),
  });

  return (
    <>
      <section className="relative overflow-hidden bg-navy-900">
        <Photo
          name="stall"
          alt=""
          eager
          className="absolute inset-0 size-full"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-900/75 to-navy-900/20" />
        <div className="relative mx-auto max-w-6xl px-4 py-20 text-white lg:py-28">
          <p className="text-sm font-bold uppercase tracking-wider text-gold-500">
            Save · Buy · Grow — together
          </p>
          <h1 className="mt-3 max-w-2xl text-[clamp(2.25rem,6vw,3.75rem)] font-extrabold leading-[1.05]">
            Your stokvel, organised. Your community, connected.
          </h1>
          <p className="mt-5 max-w-xl text-lg text-white/85">
            Run your group, buy in bulk at the group's price, and trade with
            members, spaza shops and wholesalers — from any phone.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/stokvel/start?next=%2Fstokvel%2Fnew"
              className="inline-flex h-14 items-center gap-2 rounded-2xl bg-gold-500 px-6 text-lg font-bold text-navy-800 shadow-pop hover:brightness-95"
            >
              Start a stokvel
              <ArrowRight className="size-5" aria-hidden />
            </Link>
            <Link
              to="/stokkie/stokvels"
              className="inline-flex h-14 items-center rounded-2xl bg-white/10 px-6 text-lg font-bold text-white ring-1 ring-white/40 backdrop-blur hover:bg-white/20"
            >
              Find a stokvel
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-surface">
        <div className="mx-auto max-w-6xl px-4 py-14">
          <h2 className="text-center text-3xl font-extrabold text-ink-900">
            How it works
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-center text-lg text-ink-500">
            Four steps. No app to install.
          </p>
          <div className="mt-10">
            <StepFlow steps={STEPS} />
          </div>
        </div>
      </section>

      <PhotoSection
        photo="hands"
        alt="Stokvel members with their hands joined together"
        eyebrow="Your group"
        title="Everybody sees how the month is going"
        text="The treasurer marks who has paid. Every member sees their own status and the group's progress — no more arguments about who paid what."
      >
        <div className="flex items-center gap-5 rounded-2xl bg-surface p-5 shadow-card ring-1 ring-line">
          <PaidRing paid={9} total={12} />
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-ink-500">
              November
            </p>
            <p className="text-lg font-bold text-ink-900">
              9 of 12 members paid
            </p>
            <p className="mt-1 text-sm text-ink-500">
              Recorded by the treasurer
            </p>
          </div>
        </div>
      </PhotoSection>

      <div className="bg-surface">
        <PhotoSection
          photo="fruit"
          alt="A fruit market with crates of oranges and bananas"
          eyebrow="Buy together"
          title="The more you order, the less each pays"
          text="Members add what they want. The group's total reaches the wholesaler's bulk price that nobody reaches alone. You pay the wholesaler directly when you collect."
          flip
        >
          <div className="rounded-2xl bg-canvas p-5">
            <p className="mb-4 text-sm font-semibold text-ink-600">
              Price per case — example
            </p>
            <PriceLadder
              rungs={[
                { label: "Alone", price: 220 },
                { label: "10 cases", price: 200 },
                { label: "20 cases", price: 190 },
              ]}
              reached={2}
            />
          </div>
        </PhotoSection>
      </div>

      <PhotoSection
        photo="market"
        alt="A busy community market with stalls"
        eyebrow="Marketplace"
        title="Sell to people who know you"
        text="Members advertise their products, services and businesses next to the wholesalers. Buyers message you on Stokkie Stokvel, and your number stays private until you share it."
      >
        <ul className="grid gap-3 sm:grid-cols-3">
          {[
            { icon: Store, label: "Products" },
            { icon: HandCoins, label: "Services" },
            { icon: Users, label: "Your business" },
          ].map((item) => (
            <li
              key={item.label}
              className="flex items-center gap-3 rounded-2xl bg-surface p-4 font-semibold text-ink-900 shadow-card ring-1 ring-line"
            >
              <item.icon className="size-6 text-brand-500" aria-hidden />
              {item.label}
            </li>
          ))}
        </ul>
      </PhotoSection>

      <SafetyBand />

      <PricingStrip />

      <PartnerSection />

      <section className="mx-auto max-w-6xl px-4 py-14">
        <div className="flex flex-wrap items-end gap-3">
          <h2 className="flex-1 text-3xl font-extrabold text-ink-900">
            Stokvels on Stokkie Stokvel
          </h2>
          <Link to="/stokkie/stokvels" className="font-bold text-brand-600">
            See them all →
          </Link>
        </div>
        {stokvels.isLoading ? (
          <SkeletonRows rows={3} />
        ) : stokvels.data?.length ? (
          <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {stokvels.data.slice(0, 6).map((stokvel) => (
              <li key={stokvel.id}>
                <StokvelCard stokvel={stokvel} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-4 text-lg text-ink-600">
            The first groups are joining now.{" "}
            <Link
              to="/stokvel/start?next=%2Fstokvel%2Fnew"
              className="font-bold text-brand-600"
            >
              Start yours
            </Link>
            .
          </p>
        )}
      </section>

      <section className="bg-navy-800">
        <div className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-12 text-white sm:flex-row sm:items-center">
          <Photo
            name="vegetables"
            alt=""
            className="h-24 w-full rounded-2xl sm:w-40"
          />
          <div className="flex-1">
            <h2 className="text-2xl font-bold">Run a spaza shop?</h2>
            <p className="mt-1 text-lg text-white/80">
              Use the account you already have — open “My stokvels” from your
              back office.
            </p>
          </div>
          <Link
            to="/login?mode=password"
            className="inline-flex h-14 items-center justify-center rounded-2xl bg-white px-6 text-lg font-bold text-navy-800"
          >
            Sign in to your shop
          </Link>
        </div>
      </section>
    </>
  );
}

// ─── Directory ────────────────────────────────────────────────────────────────
export function StokvelDirectory() {
  const stokvels = useQuery({
    queryKey: ["public-stokvels"],
    queryFn: () => publicApi.stokvels(),
  });
  const [query, setQuery] = useState("");
  const shown = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (stokvels.data ?? []).filter(
      (s) =>
        !term ||
        [s.name, s.area, s.province, s.kindLabel].some((v) =>
          v.toLowerCase().includes(term),
        ),
    );
  }, [stokvels.data, query]);

  return (
    <section className="mx-auto max-w-6xl px-4 py-10">
      <h1 className="text-2xl font-bold text-ink-900">Find a stokvel</h1>
      <p className="mt-1 text-ink-600">
        Groups that have put up a page. Ask to join the ones taking new members.
      </p>
      <SearchInput
        containerClassName="mt-5 max-w-xl"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Name, area or type"
        aria-label="Search stokvels"
      />
      {stokvels.isLoading ? (
        <SkeletonRows rows={4} />
      ) : shown.length ? (
        <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((stokvel) => (
            <li key={stokvel.id}>
              <StokvelCard stokvel={stokvel} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-6 text-ink-600">No stokvels found.</p>
      )}
    </section>
  );
}

function StokvelCard({ stokvel }: { stokvel: PublicStokvel }) {
  return (
    <Link
      to={`/stokkie/s/${stokvel.slug}`}
      className="flex h-full flex-col rounded-card bg-surface p-5 shadow-card transition-shadow hover:shadow-pop"
    >
      <span className="font-semibold text-ink-900">{stokvel.name}</span>
      <span className="mt-1 flex items-center gap-1 text-sm text-ink-500">
        <MapPin className="size-3.5" aria-hidden />
        {stokvel.area || stokvel.province}
      </span>
      {stokvel.about && (
        <span className="mt-3 line-clamp-3 text-sm text-ink-600">
          {stokvel.about}
        </span>
      )}
      <span className="mt-auto flex flex-wrap gap-2 pt-4 text-xs">
        <span className="rounded-full bg-canvas px-2 py-0.5 font-medium text-ink-600">
          {stokvel.kindLabel}
        </span>
        <span className="rounded-full bg-canvas px-2 py-0.5 font-medium text-ink-600">
          {stokvel.memberCount} members
        </span>
        {stokvel.acceptingMembers && (
          <span className="rounded-full bg-gold-50 px-2 py-0.5 font-semibold text-gold-700">
            Open to new members
          </span>
        )}
      </span>
    </Link>
  );
}

// ─── One stokvel's page ───────────────────────────────────────────────────────
export function PublicStokvelPage() {
  const { slug = "" } = useParams();
  const stokvel = useQuery({
    queryKey: ["public-stokvel", slug],
    queryFn: () => publicApi.stokvel(slug),
    retry: false,
  });

  if (stokvel.isLoading) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-10">
        <SkeletonRows rows={5} />
      </section>
    );
  }
  if (!stokvel.data) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-xl font-bold text-ink-900">
          This page is not available
        </h1>
        <p className="mt-2 text-ink-600">The stokvel may have taken it down.</p>
        <Link
          to="/stokkie/stokvels"
          className="mt-4 inline-block font-semibold text-brand-500"
        >
          Find another stokvel
        </Link>
      </section>
    );
  }
  const s = stokvel.data;
  const join = `/stokvel/start?next=${encodeURIComponent(`/stokvel/join?slug=${s.slug}`)}`;

  return (
    <section className="mx-auto max-w-3xl px-4 py-10">
      <p className="text-sm font-semibold uppercase tracking-wide text-gold-700">
        {s.kindLabel} stokvel · since {s.since}
      </p>
      <h1 className="mt-2 text-[clamp(1.75rem,4vw,2.5rem)] font-extrabold leading-tight text-ink-900">
        {s.name}
      </h1>
      <p className="mt-2 flex items-center gap-1 text-ink-500">
        <MapPin className="size-4" aria-hidden />
        {[s.area, s.province].filter(Boolean).join(", ")}
      </p>

      {s.about && (
        <div className="mt-6 whitespace-pre-line rounded-card bg-surface p-5 text-ink-900 shadow-card">
          {s.about}
        </div>
      )}

      <dl className="mt-6 grid gap-3 sm:grid-cols-3">
        <Fact label="Members" value={String(s.memberCount)} />
        <Fact label="Meets" value={s.meetingFrequency} />
        <Fact label="Contributions" value={s.contributionFrequency} />
      </dl>

      <div className="mt-8 rounded-card border border-line bg-surface p-5">
        {s.acceptingMembers ? (
          <>
            <h2 className="font-semibold text-ink-900">Join {s.name}</h2>
            <p className="mt-1 text-sm text-ink-600">
              Sign in with your cellphone number and ask to join. The
              chairperson approves new members.
            </p>
            <Link
              to={join}
              className="mt-4 inline-flex h-12 items-center gap-2 rounded-control bg-gold-500 px-5 font-semibold text-navy-800 shadow-card hover:brightness-95"
            >
              Ask to join
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          </>
        ) : (
          <>
            <h2 className="font-semibold text-ink-900">
              Not taking new members right now
            </h2>
            <p className="mt-1 text-sm text-ink-600">
              If you know the chairperson, ask them for the group's invite code.
            </p>
          </>
        )}
      </div>
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-card bg-surface p-4 shadow-card">
      <dt className="text-xs text-ink-500">{label}</dt>
      <dd className="mt-0.5 font-semibold text-ink-900">{value}</dd>
    </div>
  );
}

// ─── Old addresses ────────────────────────────────────────────────────────────
/** The website was at /stokfair before the rename. Links already shared keep working. */
export function OldStokfairAddress() {
  const { pathname, search } = useLocation();
  return (
    <Navigate
      replace
      to={pathname.replace(/^\/stokfair/, "/stokkie") + search}
    />
  );
}

// ─── Safety and POPIA ─────────────────────────────────────────────────────────
function SafetyBand() {
  return (
    <section className="bg-brand-700 text-white">
      <div className="mx-auto max-w-6xl px-4 py-12">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1">
            <p className="text-sm font-bold uppercase tracking-wider text-gold-500">
              Safe and private
            </p>
            <h2 className="mt-1 text-3xl font-extrabold">
              Built to protect your group
            </h2>
          </div>
          <Link
            to="/stokkie/safety"
            className="inline-flex h-12 items-center gap-2 rounded-2xl bg-white/10 px-5 font-bold ring-1 ring-white/40 hover:bg-white/20"
          >
            How we keep you safe
            <ArrowRight className="size-4" aria-hidden />
          </Link>
        </div>
        <ul className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PROMISES.map((promise) => (
            <li key={promise.title} className="flex items-start gap-4">
              <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-white/15">
                <promise.icon className="size-6 text-gold-500" aria-hidden />
              </span>
              <span>
                <span className="block text-lg font-bold">{promise.title}</span>
                <span className="block text-white/80">{promise.text}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const SAFETY: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: ShieldCheck,
    title: "We never hold your money",
    text: "Contributions go into your group's own bank account. Stokkie Stokvel only records who has paid. Nobody can take money out through the app, because there is none in it.",
  },
  {
    icon: UserCheck,
    title: "Only what we need, with your consent",
    text: "To sign up you give your cellphone number and your name. You agree to the Terms of Use and Privacy Notice before your account is made, and we use your details only to run your stokvel.",
  },
  {
    icon: Users,
    title: "Your group's business stays in your group",
    text: "Only members see a group's members, contributions and assets. The chairperson decides who joins. A group's page on this website shows only what the chairperson chose to put on it.",
  },
  {
    icon: MessageCircle,
    title: "Your number stays private",
    text: "Buyers and sellers talk inside the app. Your cellphone number is shown only when you choose to share it.",
  },
  {
    icon: Eye,
    title: "Photos without your address",
    text: "When you upload a photo, we remove the location your phone camera saves inside it, so a picture taken at home does not reveal where you live.",
  },
  {
    icon: Lock,
    title: "Secure sign-in",
    text: "You sign in with a one-time PIN sent by SMS, so there is no password to steal. Everything between your phone and us travels encrypted.",
  },
  {
    icon: FileCheck2,
    title: "A record nobody can quietly change",
    text: "Every change to roles, members and contributions is written to the group's history, with who made it and when.",
  },
  {
    icon: Trash2,
    title: "Your rights under POPIA",
    text: "You may ask to see the information we hold about you, have it corrected, or have it deleted. Ask us and we will do it.",
  },
];

export function StokkieSafety() {
  return (
    <>
      <section className="relative overflow-hidden bg-navy-900">
        <Photo
          name="hands"
          alt=""
          eager
          className="absolute inset-0 size-full"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-900/80 to-navy-900/40" />
        <div className="relative mx-auto max-w-6xl px-4 py-16 text-white lg:py-20">
          <span className="inline-flex items-center gap-2 rounded-full bg-gold-500 px-3 py-1 text-sm font-bold text-navy-800">
            <ShieldCheck className="size-4" aria-hidden />
            POPIA compliant
          </span>
          <h1 className="mt-4 max-w-2xl text-[clamp(2rem,5vw,3.25rem)] font-extrabold leading-[1.05]">
            Safe for your group. Private for you.
          </h1>
          <p className="mt-4 max-w-xl text-lg text-white/85">
            Stokkie Stokvel follows South Africa's Protection of Personal
            Information Act (POPIA). Here is what that means for you, in plain
            words.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-14">
        <ul className="grid gap-4 sm:grid-cols-2">
          {SAFETY.map((item) => (
            <li
              key={item.title}
              className="flex gap-4 rounded-card bg-surface p-5 shadow-card"
            >
              <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-brand-50">
                <item.icon className="size-6 text-brand-600" aria-hidden />
              </span>
              <span>
                <span className="block text-lg font-bold text-ink-900">
                  {item.title}
                </span>
                <span className="mt-1 block text-ink-600">{item.text}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-surface">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-12 sm:flex-row sm:items-center">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-ink-900">
              Questions about your information?
            </h2>
            <p className="mt-1 text-lg text-ink-600">
              {PARTNER.informationOfficer
                ? `Write to our Information Officer at ${PARTNER.informationOfficer}.`
                : "Ask us from inside the app, and our Information Officer will answer."}
            </p>
          </div>
          <Link
            to="/stokvel/start"
            className="inline-flex h-14 items-center justify-center rounded-2xl bg-brand-500 px-6 text-lg font-bold text-white hover:bg-brand-600"
          >
            Sign in
          </Link>
        </div>
      </section>
    </>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────────────
type TierLook = { ring: string; badge: string; icon: string };

const BRONZE_LOOK: TierLook = {
  ring: "ring-line",
  badge: "bg-warn-700 text-white",
  icon: "text-warn-700",
};

const TIER_LOOK: Record<string, TierLook> = {
  BRONZE: {
    ring: "ring-line",
    badge: "bg-warn-700 text-white",
    icon: "text-warn-700",
  },
  SILVER: {
    ring: "ring-line",
    badge: "bg-ink-400 text-white",
    icon: "text-ink-500",
  },
  GOLD: {
    ring: "ring-2 ring-gold-500",
    badge: "bg-gold-500 text-navy-800",
    icon: "text-gold-600",
  },
  PLATINUM: {
    ring: "ring-line",
    badge: "bg-navy-800 text-white",
    icon: "text-navy-800",
  },
};

function rands(price: string): string {
  const value = Number(price);
  return value === 0 ? "Free" : `R${value.toLocaleString("en-ZA")}`;
}

function PlanCard({ plan }: { plan: Plan }) {
  const look = TIER_LOOK[plan.code] ?? BRONZE_LOOK;
  const popular = plan.code === "GOLD";
  return (
    <div
      className={cn(
        "relative flex h-full flex-col rounded-card bg-surface p-6 shadow-card ring-1",
        look.ring,
      )}
    >
      {popular && (
        <span className="absolute -top-3 left-6 rounded-full bg-gold-500 px-3 py-0.5 text-xs font-bold uppercase tracking-wide text-navy-800">
          Most popular
        </span>
      )}
      <span className="flex items-center gap-2">
        <Crown className={cn("size-6", look.icon)} aria-hidden />
        <span
          className={cn(
            "rounded-full px-3 py-0.5 text-sm font-bold",
            look.badge,
          )}
        >
          {plan.label}
        </span>
      </span>
      <p className="mt-4 text-4xl font-extrabold text-ink-900">
        {rands(plan.price)}
        {Number(plan.price) > 0 && (
          <span className="text-base font-semibold text-ink-500"> / month</span>
        )}
      </p>
      <p className="mt-1 text-sm text-ink-500">
        {plan.memberLimit
          ? `Up to ${plan.memberLimit} members`
          : "Any number of members"}
      </p>
      <ul className="mt-5 flex-1 space-y-2">
        {plan.highlights.map((line) => (
          <li key={line} className="flex items-start gap-2 text-ink-900">
            <Check
              className="mt-0.5 size-5 shrink-0 text-money-600"
              aria-hidden
            />
            {line}
          </li>
        ))}
      </ul>
      <Link
        to="/stokvel/start?next=%2Fstokvel%2Fnew"
        className={cn(
          "mt-6 inline-flex h-12 items-center justify-center rounded-2xl px-5 font-bold",
          popular
            ? "bg-gold-500 text-navy-800 hover:brightness-95"
            : "bg-canvas text-ink-900 ring-1 ring-line hover:bg-surface",
        )}
      >
        {Number(plan.price) === 0 ? "Start free" : `Choose ${plan.label}`}
      </Link>
    </div>
  );
}

function usePlans() {
  return useQuery({
    queryKey: ["public-plans"],
    queryFn: () => publicApi.plans(),
    staleTime: 60 * 60 * 1000,
  });
}

/** The four plans, small, on the home page. */
function PricingStrip() {
  const plans = usePlans();
  return (
    <section className="bg-surface">
      <div className="mx-auto max-w-6xl px-4 py-14">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1">
            <p className="text-sm font-bold uppercase tracking-wider text-gold-700">
              Pricing
            </p>
            <h2 className="mt-1 text-3xl font-extrabold text-ink-900">
              Start free. Grow when your group grows.
            </h2>
          </div>
          <Link to="/stokkie/pricing" className="font-bold text-brand-600">
            Compare plans →
          </Link>
        </div>
        <ul className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          {(plans.data ?? []).map((plan) => {
            const look = TIER_LOOK[plan.code] ?? BRONZE_LOOK;
            return (
              <li key={plan.code}>
                <Link
                  to="/stokkie/pricing"
                  className={cn(
                    "flex h-full flex-col rounded-2xl bg-canvas p-4 ring-1 hover:shadow-card",
                    look.ring,
                  )}
                >
                  <span
                    className={cn(
                      "self-start rounded-full px-2.5 py-0.5 text-xs font-bold",
                      look.badge,
                    )}
                  >
                    {plan.label}
                  </span>
                  <span className="mt-3 text-2xl font-extrabold text-ink-900">
                    {rands(plan.price)}
                  </span>
                  <span className="text-sm text-ink-500">
                    {plan.memberLimit
                      ? `Up to ${plan.memberLimit} members`
                      : "Unlimited members"}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

export function StokkiePricing() {
  const plans = usePlans();
  return (
    <section className="mx-auto max-w-6xl px-4 py-12">
      <p className="text-center text-sm font-bold uppercase tracking-wider text-gold-700">
        Pricing
      </p>
      <h1 className="mt-2 text-center text-[clamp(2rem,5vw,3rem)] font-extrabold leading-tight text-ink-900">
        One price for the whole group
      </h1>
      <p className="mx-auto mt-3 max-w-xl text-center text-lg text-ink-600">
        The group pays, not each member. Every plan keeps your money in your own
        bank account — this is what the app costs, never a fee on your savings.
      </p>
      {plans.isLoading ? (
        <div className="mt-10">
          <SkeletonRows rows={4} />
        </div>
      ) : (
        <ul className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {(plans.data ?? []).map((plan) => (
            <li key={plan.code}>
              <PlanCard plan={plan} />
            </li>
          ))}
        </ul>
      )}
      <div className="mx-auto mt-12 max-w-2xl rounded-card bg-surface p-6 shadow-card">
        <h2 className="text-xl font-bold text-ink-900">How paying works</h2>
        <StepFlow
          steps={[
            {
              icon: Users,
              title: "Start free",
              text: "Every group starts on Bronze.",
            },
            {
              icon: HandCoins,
              title: "Pay by EFT",
              text: "The chairperson pays for the plan you choose.",
            },
            {
              icon: BadgeCheck,
              title: "We switch it on",
              text: "Your new plan is on the same day.",
            },
          ]}
        />
        <p className="mt-4 text-sm text-ink-500">
          Moving to a smaller plan never removes members, assets or orders — you
          just cannot add past the new limit.
        </p>
      </div>
    </section>
  );
}

// ─── Partner ──────────────────────────────────────────────────────────────────
function PartnerSection() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-14">
      <div className="grid items-center gap-8 rounded-3xl bg-surface p-6 shadow-card ring-1 ring-line md:grid-cols-[auto_1fr] md:p-10">
        <div className="grid h-32 w-full place-items-center rounded-2xl bg-navy-900 px-8 md:w-64">
          {PARTNER.logo ? (
            <img
              src={PARTNER.logo}
              alt={PARTNER.name}
              className="max-h-20 w-auto"
            />
          ) : (
            <span className="text-3xl font-extrabold tracking-tight text-white">
              {PARTNER.name}
            </span>
          )}
        </div>
        <div>
          <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gold-700">
            <Handshake className="size-4" aria-hidden />
            Our partner
          </p>
          <h2 className="mt-1 text-2xl font-extrabold text-ink-900">
            Thinking of investing as a group?
          </h2>
          <p className="mt-2 text-lg text-ink-600">
            Stokkie Stokvel does not invest money. Groups that want to can talk
            to our partner, {PARTNER.name}, directly. Any investment is between
            your group and them, on their terms.
          </p>
          {PARTNER.url && (
            <a
              href={PARTNER.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex h-12 items-center gap-2 rounded-2xl bg-navy-800 px-5 font-bold text-white hover:bg-navy-700"
            >
              Visit {PARTNER.name}
              <ArrowRight className="size-4" aria-hidden />
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
